import { ReactNode, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import {
  AccessDeniedScreen,
  AuthErrorScreen,
  AuthenticatingScreen,
  SignInScreen,
} from '../auth/thresholds'

interface AuthGuardProps {
  children: ReactNode
  requiredRole?: string
}

/**
 * Route guard for admin surfaces. Renders the SPEC-018 A4 cinematic auth
 * thresholds (ADR-084) for each unauthenticated / transient / denied / error
 * state and only renders its children once the user holds the required role.
 * Token acquisition is unchanged (ADR-085 — MSAL stays); this is presentation.
 *
 * @param children - The protected admin content.
 * @param requiredRole - Role the user must hold. Defaults to 'Admin'.
 * @returns The appropriate threshold screen, or the protected content.
 */
export function AuthGuard({ children, requiredRole = 'Admin' }: AuthGuardProps) {
  const { isAuthenticated, isLoading, user, error, login, logout } = useAuth()

  // Store return URL when not authenticated so login can bounce the user back.
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      const returnUrl = window.location.pathname + window.location.search
      sessionStorage.setItem('auth_return_url', returnUrl)
    }
  }, [isLoading, isAuthenticated, error])

  if (isLoading) {
    return <AuthenticatingScreen testId="auth-loading" />
  }

  if (error) {
    return <AuthErrorScreen error={error} onRetry={login} />
  }

  if (!isAuthenticated) {
    return <SignInScreen audience="admin" onSignIn={login} ctaTestId="login-button" />
  }

  if (requiredRole && user && !user.roles.includes(requiredRole)) {
    return (
      <AccessDeniedScreen
        audience="admin"
        testId="access-denied"
        onHome={() => {
          window.location.href = '/'
        }}
        onSignOut={logout}
      />
    )
  }

  return <>{children}</>
}

export default AuthGuard
